import type { Watchlist, WatchlistItem } from "@/schemas";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Edit2,
  MoreVertical,
  Target,
  Trash2,
  Copy,
  Move,
  TrendingUp,
  TrendingDown,
  GripVertical,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AssetLogo } from "@/components/asset/asset-logo";
import { formatPrice } from "@/lib/formatters";

interface WatchlistAssetItemProps {
  item: WatchlistItem;
  watchlists: Watchlist[];
  currentWatchlistId: string;
  resolveCurrency: (id?: string) => string;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopyTo: (watchlistId: string) => void;
  onMoveTo: (watchlistId: string) => void;
}

export function WatchlistAssetItem({
  item,
  watchlists,
  currentWatchlistId,
  resolveCurrency,
  onClick,
  onEdit,
  onDelete,
  onCopyTo,
  onMoveTo,
}: WatchlistAssetItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.item_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const otherWatchlists = watchlists.filter(
    (wl) => wl.watchlist_id !== currentWatchlistId
  );

  const dailyChange = item.daily_change_percent ?? null;
  const isPositive = dailyChange !== null && dailyChange >= 0;
  const assetCurrency =
    item.asset?.type === "stock" ||
    item.asset?.type === "etf" ||
    item.asset?.type === "fund" ||
    item.asset?.type === "crypto"
      ? item.asset.currency
      : undefined;
  const targetCurrency = resolveCurrency(
    item.latest_price_currency || assetCurrency
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-2 rounded-lg border bg-card p-2",
        isDragging && "opacity-50 shadow-lg z-50 border-primary/50"
      )}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className="touch-none text-muted-foreground hover:text-foreground p-0.5 -ml-0.5 rounded shrink-0 cursor-grab active:cursor-grabbing"
        aria-label={`Reorder ${item.asset?.symbol || item.asset_id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      {/* Clickable Content */}
      <div
        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
        onClick={onClick}
      >
        {/* Logo */}
        {item.asset && <AssetLogo asset={item.asset} size="sm" />}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-xs truncate">
              {item.asset?.symbol || item.asset_id}
            </span>
            {item.target_price !== undefined && item.target_price !== null && (
              <Target className="h-2.5 w-2.5 text-amber-500 shrink-0" />
            )}
          </div>
          {item.target_price !== undefined && item.target_price !== null && (
            <p className="text-[10px] text-amber-600 font-medium truncate mt-0.5">
              Target {formatPrice(item.target_price, targetCurrency)}
            </p>
          )}
          {item.note && (
            <p className="text-[9px] text-muted-foreground truncate mt-0.5">
              {item.note}
            </p>
          )}
        </div>

        {/* Price & Change */}
        <div className="flex flex-col items-end shrink-0">
          {item.latest_price !== undefined && item.latest_price !== null && (
            <span className="text-xs font-mono font-medium">
              {formatPrice(
                item.latest_price,
                resolveCurrency(item.latest_price_currency || assetCurrency)
              )}
            </span>
          )}
          {dailyChange !== null && (
            <span
              className={cn(
                "text-[10px] font-semibold flex items-center gap-0.5",
                isPositive ? "text-green-500" : "text-red-500"
              )}
            >
              {isPositive ? (
                <TrendingUp className="h-2.5 w-2.5" />
              ) : (
                <TrendingDown className="h-2.5 w-2.5" />
              )}
              {isPositive ? "+" : ""}
              {dailyChange.toFixed(2)}%
            </span>
          )}
        </div>
      </div>

      {/* Actions Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Edit2 className="h-3 w-3 mr-2" />
            Edit Note/Target
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Move/Copy</DropdownMenuLabel>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Copy className="h-3 w-3 mr-2" />
              Copy to...
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {otherWatchlists.length > 0 ? (
                otherWatchlists.map((wl) => (
                  <DropdownMenuItem
                    key={wl.watchlist_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyTo(wl.watchlist_id);
                    }}
                  >
                    {wl.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>
                  No other watchlists
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Move className="h-3 w-3 mr-2" />
              Move to...
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {otherWatchlists.length > 0 ? (
                otherWatchlists.map((wl) => (
                  <DropdownMenuItem
                    key={wl.watchlist_id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveTo(wl.watchlist_id);
                    }}
                  >
                    {wl.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>
                  No other watchlists
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3 w-3 mr-2" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Compact preview for the drag overlay */
export function WatchlistAssetItemOverlay({
  item,
  resolveCurrency,
}: {
  item: WatchlistItem;
  resolveCurrency: (id?: string) => string;
}) {
  const assetCurrency =
    item.asset?.type === "stock" ||
    item.asset?.type === "etf" ||
    item.asset?.type === "fund" ||
    item.asset?.type === "crypto"
      ? item.asset.currency
      : undefined;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-card p-2 shadow-xl w-64 scale-105">
      {item.asset && <AssetLogo asset={item.asset} size="sm" />}
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-xs truncate">
          {item.asset?.symbol || item.asset_id}
        </span>
      </div>
      {item.latest_price !== undefined && item.latest_price !== null && (
        <span className="text-xs font-mono font-medium">
          {formatPrice(
            item.latest_price,
            resolveCurrency(item.latest_price_currency || assetCurrency)
          )}
        </span>
      )}
    </div>
  );
}
